import { CloseApiClient } from "@app/closeApiClient";
import { downloadAsCsvFile } from "@app/csvHelper";
import {
  QUERY_LEADS_WITH_EMAILS,
  QUERY_LEADS_WITH_NAME,
  QUERY_LEADS_WITH_PHONES,
  QUERY_LEADS_WITH_CONTACT_NAMES,
  QUERY_LEADS_WITH_URLS,
  QUERY_LEADS_WITH_ADDRESS_1,
} from "@app/tools/leads/findLeadDuplicates/searchQueries";
import { Lead } from "@app/types/lead";
import { toArrayFromAsync } from "@app/utils/arrayUtils";
import { FormValues } from "@app/tools/leads/findLeadDuplicates/findLeadDuplicatesPage";

const DUPLICATE_COUNT_LIMIT: number = 10;

async function getLeadCount(api: CloseApiClient) {
  const response = (await api.get("lead", {
    query: { _fields: "id", _limit: "0" },
  })) as { total_results: number };
  return response.total_results;
}

export async function findDuplicates(
  values: FormValues,
  addOutputLine: (message: string) => void,
  clearOutput: () => void,
): Promise<void> {
  clearOutput();

  const api = new CloseApiClient(values.apiKey);
  const organizationName = await api.getOrganizationName();

  const deduplicateBy = values.deduplicateBy;
  const deduplicateByFormatted = (
    deduplicateBy.charAt(0).toUpperCase() + deduplicateBy.slice(1)
  ).replace("_", " ");

  addOutputLine(
    `Finding lead duplicates by ${deduplicateByFormatted.toLowerCase()} in '${organizationName}'...`,
  );

  const leadCount = await getLeadCount(api);
  const approximateDurationMin = leadCount < 10000 ? "couple" : "couple dozen";

  addOutputLine(
    `Fetching ${leadCount} leads (this may take a ${approximateDurationMin} minutes to complete)...`,
  );
  const leads = await fetchLeads(api, deduplicateBy, addOutputLine);

  addOutputLine(`Finding duplicates...`);
  const duplicates = findDuplicatesForLeads(leads, deduplicateBy);

  // If there are no duplicates, we're done
  const duplicateCount = Object.values(duplicates).filter(
    (leads) => leads.length > 1,
  ).length;
  if (duplicateCount === 0) {
    addOutputLine("No duplicates found.");
    addOutputLine("Done!");
    return;
  }

  addOutputLine(`Found ${duplicateCount} duplicates.`);

  // Create CSV row for each duplicate
  const csvDuplicates = generateCsvDuplicates(
    duplicates,
    deduplicateBy,
    deduplicateByFormatted,
  );

  // Sort duplicates by dedupe field so they are grouped together
  csvDuplicates.sort((a, b) =>
    (a[deduplicateByFormatted] || "")
      .toLowerCase()
      .localeCompare((b[deduplicateByFormatted] || "").toLowerCase()),
  );

  // Get a list of duplicate keys above the warning limit
  const duplicatesOverLimit = Object.entries(duplicates).filter(
    ([, leadDuplicates]) => leadDuplicates.length > DUPLICATE_COUNT_LIMIT,
  );

  // Output error message if any keys exceed the limit
  if (duplicatesOverLimit.length > 0) {
    addOutputLine("");
    addOutputLine("***ATTENTION***");
    addOutputLine(
      `We've detected some leads in your system that have an unusually high number of duplicates.`,
    );
    addOutputLine(
      `Please review these before sending your CSV to Close Support to merge.`,
    );
    addOutputLine("");

    addOutputLine(
      `Specifically, the following leads have more than ${DUPLICATE_COUNT_LIMIT} duplicates:`,
    );
    for (const [key, value] of duplicatesOverLimit) {
      addOutputLine(
        `"${key}" - ${value.length} ${value.length > 1 ? "duplicates" : "duplicate"}`,
      );
    }
    addOutputLine("");
  }

  addOutputLine(`Writing duplicates to CSV...`);
  const fileName = `${organizationName} ${deduplicateByFormatted} Duplicates`;
  downloadAsCsvFile(fileName, csvDuplicates);

  addOutputLine("Done!");
}

async function fetchLeads(
  api: CloseApiClient,
  deduplicateBy: string,
  addOutputLine: (message: string) => void,
) {
  let query;

  // Select the appropriate query based on deduplication type
  if (deduplicateBy === "lead_name") {
    query = QUERY_LEADS_WITH_NAME;
  } else if (["email_address", "email_domain"].includes(deduplicateBy)) {
    query = QUERY_LEADS_WITH_EMAILS;
  } else if (deduplicateBy === "phone_number") {
    query = QUERY_LEADS_WITH_PHONES;
  } else if (["url", "url_domain"].includes(deduplicateBy)) {
    query = QUERY_LEADS_WITH_URLS;
  } else if (deduplicateBy === "contact_name") {
    query = QUERY_LEADS_WITH_CONTACT_NAMES;
  } else if (deduplicateBy === "address_1") {
    query = QUERY_LEADS_WITH_ADDRESS_1;
  }

  // Define fields to fetch based on deduplication type
  // because we don't need all the fields for all the deduplication types
  const leadFields = [
    "id",
    "name",
    "display_name",
    "contacts",
    "status_label",
    "date_created",
    "last_lead_status_change_date",
  ];

  if (["url", "url_domain"].includes(deduplicateBy)) {
    leadFields.push("url");
  }

  if (deduplicateBy === "address_1") {
    leadFields.push("addresses");
  }

  const leads = await toArrayFromAsync(
    api.searchAll(
      {
        query: query,
        _fields: {
          lead: leadFields,
        },
        include_counts: true,
      },
      addOutputLine,
    ),
  );

  addOutputLine(`Found ${leads.length} leads.`);
  return leads;
}

function findDuplicatesForLeads(
  leads: Lead[],
  deduplicateBy: string,
): Record<string, Lead[]> {
  const duplicates: Record<string, Lead[]> = Object.create(null);

  function addDuplicate(key: string, lead: Lead) {
    if (!key || key.trim() === "") return; // Skip empty keys

    const normalizedKey = key.trim().toLowerCase();
    duplicates[normalizedKey] = duplicates[normalizedKey] || [];

    // Only add the lead if it doesn't already exist in the duplicates array for this key.
    //
    // This prevents duplicates from being added multiple times if they have the same key
    // more than once (e.g. if they have the same email address on multiple contacts).
    const leadExists = duplicates[normalizedKey].some(
      (existingLead) => existingLead.id === lead.id,
    );
    if (!leadExists) {
      duplicates[normalizedKey].push(lead);
    }
  }

  function findDuplicatesForLead(lead: Lead) {
    if (deduplicateBy === "lead_name") {
      const displayName = lead.display_name.trim().toLowerCase();

      const isUntitledLead = !lead.name && displayName === "untitled";
      if (isUntitledLead) {
        // Skip untitled leads as they don't have a meaningful name
        // we can use for deduplication
        return;
      }

      addDuplicate(displayName, lead);
    } else if (deduplicateBy === "address_1") {
      if (lead.addresses && lead.addresses.length > 0) {
        lead.addresses.forEach((address) => {
          if (address.address_1) {
            addDuplicate(address.address_1, lead);
          }
        });
      }
    } else if (deduplicateBy === "url") {
      if (lead.url) {
        addDuplicate(lead.url, lead);
      }
    } else if (deduplicateBy === "url_domain") {
      if (lead.url) {
        try {
          const domain = new URL(lead.url).hostname;
          addDuplicate(domain, lead);
        } catch (e) {
          // Invalid URL, skip
        }
      }
    }

    lead.contacts.forEach((contact) => {
      if (deduplicateBy === "contact_name" && contact.name) {
        addDuplicate(contact.name, lead);
      }

      if (deduplicateBy === "email_address") {
        contact.emails.forEach((email) => addDuplicate(email.email, lead));
      }

      if (deduplicateBy === "email_domain") {
        contact.emails.forEach((email) => {
          const parts = email.email.split("@");
          if (parts.length === 2 && parts[1]) {
            addDuplicate(parts[1], lead);
          }
        });
      }

      if (deduplicateBy === "phone_number") {
        contact.phones.forEach((phone) => addDuplicate(phone.phone, lead));
      }
    });
  }

  leads.forEach(findDuplicatesForLead);

  return duplicates;
}

function generateCsvDuplicates(
  duplicates: Record<string, Lead[]>,
  deduplicateBy: string,
  deduplicateByFormatted: string,
): any[] {
  return Object.entries(duplicates).flatMap(([dedupeKey, leadDuplicates]) =>
    leadDuplicates.length > 1 // If there is only one lead, it's not a duplicate
      ? leadDuplicates.map((lead) => {
          const updatedLead = { ...lead, [deduplicateBy]: dedupeKey };
          return generateCsvRow(
            updatedLead,
            deduplicateBy,
            deduplicateByFormatted,
          );
        })
      : [],
  );
}

function generateCsvRow(
  lead: Lead,
  deduplicateBy: string,
  deduplicateByFormatted: string,
): any {
  // Add the dedupe field as the first property
  const csvRow: any = {
    [deduplicateByFormatted]: lead[deduplicateBy],
    "Lead Name": lead.display_name,
    "Status Label": lead.status_label,
    "Lead ID": lead.id,
    "Lead Date Created": lead.date_created,
    "Last Lead Status Change Date": lead.last_lead_status_change_date,
  };
  return csvRow;
}
